import { sanitizeDashboardDateRangeForQuery } from "./dashboardDateRange";

/** Day / week / month segmented control → API `view` param */
export const DASHBOARD_VIEW_MAP = { D: "daily", W: "weekly", M: "monthly" };

/** API `view` param → segmented control value */
export function mapViewToTimeFrame(view) {
  const normalized = String(view || "").toLowerCase();
  if (normalized === "custom") return "C";
  for (const [timeFrame, apiView] of Object.entries(DASHBOARD_VIEW_MAP)) {
    if (apiView === normalized) return timeFrame;
  }
  return null;
}

/**
 * Canonical query string so SWR keys match across tabs regardless of insertion order.
 */
export function canonicalQueryString(input) {
  const raw =
    input instanceof URLSearchParams ? input.toString()
      : typeof input === "string" ? input : "";
  const sp = new URLSearchParams(raw);
  const pairs = [...sp.entries()].filter(([, v]) => v != null && v !== "");
  pairs.sort(([a], [b]) => (a === b ? 0 : a < b ? -1 : 1));
  const next = new URLSearchParams();
  pairs.forEach(([k, v]) => next.append(k, v));
  return next.toString();
}

export function dashboardSwrKey(path, canonicalQueryPart = "") {
  const normalizedPath = path.endsWith("/") ? path : `${path}/`;
  const q = String(canonicalQueryPart || "").trim();
  return q ? `${normalizedPath}?${q}` : normalizedPath;
}

export function buildDashboardSummaryQueryString({ timeFrame, scope, filters }) {
  const p = new URLSearchParams();
  const view = DASHBOARD_VIEW_MAP[timeFrame];
  if (view) p.set("view", view);
  if (scope === "domestic" || scope === "international") p.set("region", scope);
  const { startStr, endStr } = sanitizeDashboardDateRangeForQuery(filters?.startDate, filters?.endDate);
  if (startStr) p.set("start_date", startStr);
  if (endStr) p.set("end_date", endStr);
  return canonicalQueryString(p);
}

export function buildDashboardTrendQueryString({ timeFrame, scope, filters }) {
  const p = new URLSearchParams();
  const view = DASHBOARD_VIEW_MAP[timeFrame];
  if (view) p.set("view", view);
  if (scope === "domestic" || scope === "international") p.set("region", scope);
  const { startStr, endStr } = sanitizeDashboardDateRangeForQuery(filters?.startDate, filters?.endDate);
  if (startStr) p.set("start_date", startStr);
  if (endStr) p.set("end_date", endStr);
  return canonicalQueryString(p);
}

/** GET dashboard/weekly-trend/ — view is weekly | custom only */
export function buildWeeklyTrendQueryString({ scope, filters }) {
  const p = new URLSearchParams();
  const { startStr, endStr } = sanitizeDashboardDateRangeForQuery(filters?.startDate, filters?.endDate);
  if (startStr && endStr) {
    p.set("view", "custom");
    p.set("start_date", startStr);
    p.set("end_date", endStr);
  } else {
    p.set("view", "weekly");
  }
  if (scope === "domestic" || scope === "international") p.set("region", scope);
  return canonicalQueryString(p);
}

export function augmentDashboardQueryString(baseCanonical, extra) {
  const p = new URLSearchParams(baseCanonical || "");
  if (extra && typeof extra === "object") {
    for (const [k, v] of Object.entries(extra)) {
      if (v == null || v === "") p.delete(k);
      else p.set(k, String(v));
    }
  }
  return canonicalQueryString(p);
}

export function buildDashboardSectionQueryString(baseCanonical, localDates) {
  const p = new URLSearchParams(baseCanonical || "");
  const hasLocalDates = Boolean(localDates?.startDate || localDates?.endDate);
  if (!hasLocalDates) return canonicalQueryString(p);

  const { startStr, endStr } = sanitizeDashboardDateRangeForQuery(
    localDates?.startDate,
    localDates?.endDate
  );
  if (startStr) p.set("start_date", startStr);
  else p.delete("start_date");
  if (endStr) p.set("end_date", endStr);
  else p.delete("end_date");
  return canonicalQueryString(p);
}

export function buildDashboardPlansQueryString({ scope, filters, planType }) {
  const p = new URLSearchParams();
  if (scope === "domestic" || scope === "international") p.set("region", scope);
  if (planType && planType !== "all") p.set("plan_type", planType);
  const { startStr, endStr } = sanitizeDashboardDateRangeForQuery(filters?.startDate, filters?.endDate);
  if (startStr) p.set("start_date", startStr);
  if (endStr) p.set("end_date", endStr);
  return canonicalQueryString(p);
}

/** Individual operator dashboard — same view/region/dates as admin summary. */
export function buildIndividualOperatorQueryString({ timeFrame, scope, filters }) {
  return buildDashboardSummaryQueryString({ timeFrame, scope, filters });
}

/** Policy status endpoint also accepts from_date / to_date / date_type. */
export function buildIndividualOperatorPolicyStatusQueryString({ timeFrame, scope, filters }) {
  const p = new URLSearchParams();
  const view = DASHBOARD_VIEW_MAP[timeFrame];
  if (view) {
    p.set("view", view);
    p.set("date_type", view);
  }
  if (scope === "domestic" || scope === "international") p.set("region", scope);
  const { startStr, endStr } = sanitizeDashboardDateRangeForQuery(
    filters?.startDate,
    filters?.endDate
  );
  if (startStr) {
    p.set("start_date", startStr);
    p.set("from_date", startStr);
  }
  if (endStr) {
    p.set("end_date", endStr);
    p.set("to_date", endStr);
  }
  return canonicalQueryString(p);
}

export const DASHBOARD_ADMIN_SWR_CONFIG = Object.freeze({
  dedupingInterval: 60000,
  revalidateOnFocus: false,
  shouldRetryOnError: false,
});

export const DASHBOARD_OPERATOR_SWR_CONFIG = Object.freeze({
  dedupingInterval: 5000,
  revalidateOnFocus: true,
  shouldRetryOnError: false,
});